import { Facet } from "@/types/productListing";
import { TertiaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { ChevronDownOutlined as ChevronDown24PxOutlinedIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronDownOutlined";
import { ChevronUpOutlined as ChevronUp24PxOutlinedIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronUpOutlined";
import styles from "./FilterButtonWithDropdown.module.scss";
import classNames from "classnames/bind";
import { FilterDropdown } from "../FilterDropdown/FilterDropdown";
import { useRef, useEffect } from "react";
import { sendMonetateEvent } from "@/utils/sendMonetateEvent";

const cx = classNames.bind(styles);

type FilterButtonWithDropdownProps = {
  facet: Facet;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
};

export function FilterButtonWithDropdown({
  facet,
  isOpen,
  onToggle,
  onClose,
}: FilterButtonWithDropdownProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    // Handle ESC key
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen, onClose]);

  return (
    <div
      ref={containerRef}
      className={cx("filterButtonWithDropdown")}
      data-testid={`filter-button-container-${facet.dimensionName}`}
    >
      <TertiaryButton
        className={cx("filterButton", {
          "filterButton--open": isOpen,
        })}
        data-testid={`filter-button-${facet.dimensionName}`}
        onClick={() => {
          sendMonetateEvent("plp.facets.interaction");
          sendMonetateEvent("plp.facets.interaction.top5");
          onToggle();
        }}
        aria-expanded={isOpen}
        aria-haspopup="true"
        small
        variant="clear-dark"
      >
        <span className={styles.buttonContent}>
          <span className={styles.facetName}>{facet.name}</span>
          <span className={styles.chevronIcon}>
            {isOpen ? (
              <ChevronUp24PxOutlinedIcon size="small" />
            ) : (
              <ChevronDown24PxOutlinedIcon size="small" />
            )}
          </span>
        </span>
      </TertiaryButton>

      {isOpen && <FilterDropdown facet={facet} isOpen={isOpen} />}
    </div>
  );
}
